/**
 * The skill leaderboard's pure rules, after skills.sh: which skills a view
 * shows, in what order, and the words and marks each row carries. Everything
 * reads the server's board (state/skillBoard) — counts are the organization's
 * real skill loads; nothing here invents a number.
 *
 * @module components/skills/skillBoardLogic
 */
import type { SkillBoardEntry } from "../../state/skillBoard";
import { getSkillSource, SKILL_SOURCE_LABEL, type SkillFilter } from "../connections/skillContent";

/** The browse views; each is backed by the board's own fields. */
export const SKILL_VIEWS = [
  { id: "all", label: "All skills", tab: "All" },
  { id: "most-used", label: "Most used", tab: "Most used" },
  { id: "recent", label: "Recently added", tab: "Recent" },
] as const;
export type SkillView = (typeof SKILL_VIEWS)[number]["id"];

export type SkillSource = Exclude<SkillFilter, "all">;

/** The owner column, as skills.sh's `owner/repo`: who a skill comes from. */
export const SYSTEM_OWNER = "amoreg";

export function skillSource(entry: Pick<SkillBoardEntry, "id" | "key" | "scope">): SkillSource {
  return getSkillSource({ id: entry.id ?? entry.key, scope: entry.scope });
}

export function skillOwner(
  entry: Pick<SkillBoardEntry, "id" | "key" | "scope" | "jurisdictions">,
  organizationName: string | null | undefined,
): string {
  const source = skillSource(entry);
  if (source === "system") return SYSTEM_OWNER;
  if (source === "organization") return organizationName?.trim() || "Your organisation";
  return entry.jurisdictions.length > 0
    ? entry.jurisdictions.join(", ")
    : SKILL_SOURCE_LABEL.jurisdiction;
}

export interface SkillTopic {
  readonly name: string;
  readonly count: number;
}

/** The categories the board's skills carry, with how many each; alphabetical. */
export function skillTopics(
  entries: ReadonlyArray<Pick<SkillBoardEntry, "category">>,
): SkillTopic[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const topic = entry.category?.trim();
    if (topic) counts.set(topic, (counts.get(topic) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export interface SkillBoardFilters {
  readonly view: SkillView;
  readonly source: SkillFilter;
  readonly topic: string | null;
  readonly query: string;
}

export const NO_SKILL_FILTERS: SkillBoardFilters = {
  view: "all",
  source: "all",
  topic: null,
  query: "",
};

export interface RankedSkill {
  /** 1-based place in the view, as the leaderboard's `#`. */
  readonly rank: number;
  readonly entry: SkillBoardEntry;
  readonly owner: string;
  readonly source: SkillSource;
}

const addedMs = (entry: Pick<SkillBoardEntry, "addedAt">) => {
  const parsed = entry.addedAt ? Date.parse(entry.addedAt) : Number.NaN;
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * The rows a view shows. All: every skill, most loaded first. Most used: only
 * skills loaded in the window. Recently added: library skills newest first
 * (the agent's own skills ship with it and have no date). Source, topic and
 * search narrow any view; ranks follow the view's order.
 */
export function rankSkills(
  entries: ReadonlyArray<SkillBoardEntry>,
  filters: SkillBoardFilters,
  organizationName: string | null | undefined,
): RankedSkill[] {
  const query = filters.query.trim().toLocaleLowerCase();
  const rows = entries
    .map((entry) => ({
      entry,
      owner: skillOwner(entry, organizationName),
      source: skillSource(entry),
    }))
    .filter(({ entry, owner, source }) => {
      if (filters.source !== "all" && source !== filters.source) return false;
      if (filters.topic !== null && entry.category?.trim() !== filters.topic) return false;
      if (filters.view === "most-used" && entry.uses === 0) return false;
      if (filters.view === "recent" && addedMs(entry) === null) return false;
      if (!query) return true;
      return [
        entry.name,
        entry.slug,
        entry.description,
        entry.category ?? "",
        owner,
        SKILL_SOURCE_LABEL[source],
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  const byName = (left: SkillBoardEntry, right: SkillBoardEntry) =>
    left.name.localeCompare(right.name) || left.key.localeCompare(right.key);
  rows.sort((left, right) =>
    filters.view === "recent"
      ? (addedMs(right.entry) ?? 0) - (addedMs(left.entry) ?? 0) || byName(left.entry, right.entry)
      : right.entry.uses - left.entry.uses || byName(left.entry, right.entry),
  );
  return rows.map((row, index) => ({ ...row, rank: index + 1 }));
}

/** How many skills each source holds, for the filter column. */
export function sourceCounts(
  entries: ReadonlyArray<SkillBoardEntry>,
): Readonly<Record<SkillFilter, number>> {
  const counts = { all: entries.length, system: 0, organization: 0, jurisdiction: 0 };
  for (const entry of entries) counts[skillSource(entry)] += 1;
  return counts;
}

/** "1,204", "12.4K": the leaderboard's compact count. */
export function formatUses(value: number): string {
  if (value >= 10_000)
    return `${(value / 1000).toFixed(value >= 100_000 ? 0 : 1).replace(/\.0$/, "")}K`;
  return value.toLocaleString("en-GB");
}

/**
 * A sparkline's SVG path over `width × height`, one point per week, zero on the
 * floor. A flat or empty series draws the floor line; the caller decides
 * whether a skill never used shows a line at all.
 */
export function sparklinePath(
  values: ReadonlyArray<number>,
  width: number,
  height: number,
  inset = 1.5,
): string {
  if (values.length === 0) return "";
  const max = Math.max(...values, 0);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const y = (value: number) =>
    max === 0 ? height - inset : inset + (height - 2 * inset) * (1 - value / max);
  return values
    .map(
      (value, index) =>
        `${index === 0 ? "M" : "L"}${(index * step).toFixed(2)} ${y(value).toFixed(2)}`,
    )
    .join(" ");
}

/** The skill page's address segment: the slug, as skills.sh's `/owner/repo/skill`. */
export const findSkillBySlug = (
  entries: ReadonlyArray<SkillBoardEntry>,
  slug: string,
): SkillBoardEntry | null => entries.find((entry) => entry.slug === slug) ?? null;

/**
 * What "Use in chat" puts in the composer. The agent's own skills are invoked
 * by their `$name` token (the composer's skill menu inserts the same). The
 * agent has no tool that reads a library skill, so a library skill travels as
 * its method, inline; without the method only its name is named.
 */
export function skillChatPrompt(
  entry: Pick<SkillBoardEntry, "kind" | "name" | "slug">,
  body: string | null,
): string {
  if (entry.kind === "agent") return `$${entry.slug} `;
  const method = body?.trim();
  return method
    ? `Follow our “${entry.name}” skill for this task. Its method:\n\n${method}\n\nThe task: `
    : `Follow our “${entry.name}” skill (${entry.slug}) for this task: `;
}

/**
 * What "Edit in chat" puts in the composer, as the library's "Create skill"
 * does. The current method rides along for the same reason as above: the
 * agent can update a library skill but cannot read one.
 */
export function editInChatPrompt(
  entry: Pick<SkillBoardEntry, "name" | "slug">,
  body: string | null,
): string {
  const method = body?.trim();
  return (
    `Update our organization's “${entry.name}” skill (${entry.slug}) in the skill library. ` +
    "Agree the change with me, then save it." +
    (method ? ` Its current method:\n\n${method}\n\n` : " ") +
    "The change: "
  );
}
